import {Group, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import CreateDashboardFromQueryButton, {
  canUserCreateOrEditInsightsDashboards,
} from 'llm/chat-library/tool-details/CreateDashboardFromQueryButton';
import {
  formatLoadSkillInput,
  parseLoadSkillResponse,
} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {
  formatManageSkillInput,
  getManageSkillFiles,
  ParsedSkillResponse,
  parseManageSkillResponse,
  parseSkillFrontmatter,
  SkillFile,
  SKILL_MD_PATH,
} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {
  ParsedProgressToolCall,
  SearchNotebookEntriesToolCall,
  MCPToolCall,
  parseProgressToolCall,
} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {ToolType, getToolIcon, isMCPTool} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_PROXIMA_18,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// ============================================================================
// Lightweight rendering blocks
// ============================================================================

function MonoBlock({text}: {text: string}): JSX.Element {
  const {classes} = useSkillStyles();
  return <div className={classes.codeBlock}>{text}</div>;
}

const MIN_EDITOR_HEIGHT = 120;
const MAX_EDITOR_HEIGHT = 320;
const BORDER_OFFSET = 2;

const useSkillStyles = createStyles((theme) => ({
  codeBlock: {
    ...FONT_PROXIMA_12,
    color: theme.colors.gray[7],
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    whiteSpace: 'pre-wrap',
    fontFamily: theme.fontFamilyMonospace,
    lineHeight: 1.4,
    maxHeight: 250,
    overflowY: 'auto',
  },
  editorContainer: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
  },
  markdownContainer: {
    ...FONT_PROXIMA_14,
    padding: `0 ${theme.spacing.md}`,
    backgroundColor: theme.white,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  fileBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  filePath: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: theme.colors.gray[7],
    fontFamily: theme.fontFamilyMonospace,
  },
  skillMdBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
    padding: theme.spacing.md,
  },
  frontmatterBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    paddingBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
  },
  frontmatterField: {
    ...FONT_PROXIMA_14,
    color: theme.colors.gray[7],
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'baseline',
  },
  frontmatterLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    flexShrink: 0,
  },
  frontmatterValue: {
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
}));

function ReadOnlyCodeBlock({code, language}: {code: string; language: MonacoLanguage}): JSX.Element {
  const {classes} = useSkillStyles();
  const [height, setHeight] = useState(MIN_EDITOR_HEIGHT);

  return (
    <div className={classes.editorContainer}>
      <MonacoEditor
        value={code}
        language={language}
        height={height}
        width="100%"
        theme="vs"
        options={{readOnly: true, minimap: {enabled: false}, scrollBeyondLastLine: false, wordWrap: 'on'}}
        onChangeContentSize={(e) => {
          setHeight(
            Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
          );
        }}
      />
    </div>
  );
}

function MarkdownBlock({markdown}: {markdown: string}): JSX.Element {
  const {classes} = useSkillStyles();
  return (
    <div className={classes.markdownContainer}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

function SkillMdBlock({content}: {content: string}): JSX.Element {
  const {classes} = useSkillStyles();
  const {name, description, body} = parseSkillFrontmatter(content);
  if (name == null && description == null) {
    return <MarkdownBlock markdown={body} />;
  }
  return (
    <div className={classes.skillMdBlock}>
      <div className={classes.frontmatterBlock}>
        {name != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Name:</span>
            <span className={classes.frontmatterValue}>{name}</span>
          </div>
        )}
        {description != null && (
          <div className={classes.frontmatterField}>
            <span className={classes.frontmatterLabel}>Description:</span>
            <span className={classes.frontmatterValue}>{description}</span>
          </div>
        )}
      </div>
      {body.trim().length > 0 && <LLMMarkdownSection markdown={body} />}
    </div>
  );
}

function FileContentBlock({file}: {file: SkillFile}): JSX.Element {
  const {classes} = useSkillStyles();
  const lowerPath = file.path.toLowerCase();
  let content: JSX.Element;
  if (file.path === SKILL_MD_PATH) {
    content = <SkillMdBlock content={file.content} />;
  } else if (lowerPath.endsWith('.md')) {
    content = <MarkdownBlock markdown={file.content} />;
  } else if (lowerPath.endsWith('.py')) {
    content = <ReadOnlyCodeBlock code={file.content} language="python" />;
  } else if (lowerPath.endsWith('.sql')) {
    content = <ReadOnlyCodeBlock code={file.content} language="sql" />;
  } else if (lowerPath.endsWith('.json')) {
    content = <ReadOnlyCodeBlock code={file.content} language="json" />;
  } else {
    content = <MonoBlock text={file.content} />;
  }
  return (
    <div className={classes.fileBlock}>
      <div className={classes.filePath}>{file.path}</div>
      {content}
    </div>
  );
}

function SkillFileList({parsed}: {parsed: ParsedSkillResponse}): JSX.Element {
  const {classes} = useSkillStyles();
  return (
    <div className={classes.fileList}>
      {parsed.files.map((file) => (
        <FileContentBlock key={file.path} file={file} />
      ))}
    </div>
  );
}

// ============================================================================
// Tool input/output formatting
// ============================================================================

function formatArgsForDisplay(args: Record<string, unknown>): string {
  return Object.entries(args)
    .map(([key, value]) => {
      if (typeof value === 'object' && value != null) {
        return `${key}: ${JSON.stringify(value, null, 2)}`;
      }
      return `${key}: ${value}`;
    })
    .join('\n');
}

function formatSearchNotebookEntriesInput(toolCall: SearchNotebookEntriesToolCall): string {
  const parts: Array<string> = [];

  const queries = Array.isArray(toolCall.queries) ? toolCall.queries : [];
  if (queries.length) {
    parts.push(`Queries: ${queries.map((q) => `"${q}"`).join(', ')}`);
  }

  const fields: Array<[string | undefined, string]> = [
    [toolCall.authoredBy?.join(', '), 'Authored By'],
    [toolCall.createdAfter, 'Created After'],
    [toolCall.createdBefore, 'Created Before'],
    [toolCall.modifiedAfter, 'Modified After'],
    [toolCall.modifiedBefore, 'Modified Before'],
    [toolCall.reviewCompletedAfter, 'Review Completed After'],
    [toolCall.reviewCompletedBefore, 'Review Completed Before'],
    [toolCall.reviewStatus, 'Review Status'],
    [toolCall.reviewRole, 'Review Role'],
    [toolCall.inProjectOrFolder, 'In Project/Folder'],
    [toolCall.inStudy, 'In Study'],
  ];

  for (const [value, label] of fields) {
    if (value) {
      parts.push(`${label}: ${value}`);
    }
  }

  if (toolCall.offset !== undefined && toolCall.offset > 0) {
    parts.push(`Offset: ${toolCall.offset}`);
  }

  return parts.length > 0 ? parts.join('\n') : 'No search parameters';
}

function isMCPToolCall(parsed: ParsedProgressToolCall): parsed is MCPToolCall {
  return isMCPTool(parsed.tool);
}

/**
 * Returns structured input rendering for a tool, or null if the tool has no
 * structured input (in which case the caller should render a fallback).
 *
 * Also used by ToolCallItem in ChatProgress to determine whether a tool
 * has structured detail worth showing in the activity modal.
 */
export function getToolInputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    if (!parsed.args || Object.keys(parsed.args).length === 0) {
      return null;
    }
    return <MonoBlock text={formatArgsForDisplay(parsed.args)} />;
  }

  switch (parsed.tool) {
    case ToolType.SearchTool:
      return (
        <MonoBlock
          text={`Query: "${parsed.query || ''}"${parsed.objectTypes?.length ? `\nObject Types: ${parsed.objectTypes.join(', ')}` : ''}`}
        />
      );
    case ToolType.SearchNotebookEntries:
      return <MonoBlock text={formatSearchNotebookEntriesInput(parsed)} />;
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
      return <ReadOnlyCodeBlock code={parsed.query || ''} language="pgsql" />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackInputBlock(toolCall);
    case ToolType.ReadBenchlingDocumentTool:
      return <MonoBlock text={`Document ID: ${parsed.documentId || 'N/A'}`} />;
    case ToolType.AttachmentTool:
      return <MonoBlock text={`Attachment ID: ${parsed.attachmentId || 'N/A'}`} />;
    case ToolType.PublicDataSubAgent:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;
    case ToolType.IdsToModelsTool:
      return (
        <MonoBlock
          text={`Object IDs: ${parsed.objectIds?.join(', ') || 'N/A'}\nInclude Relevant Items: ${parsed.includeRelevantItems ?? false}`}
        />
      );
    case ToolType.PlottingTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.GenerateImageTool:
      return <MonoBlock text={`Prompt: "${parsed.prompt || ''}"`} />;
    case ToolType.CustomCodeTool:
    case ToolType.RunCodeTool:
      return <ReadOnlyCodeBlock code={parsed.code || 'No code provided'} language="python" />;
    case ToolType.LoadSkill:
      return <MonoBlock text={formatLoadSkillInput(parsed)} />;
    case ToolType.ManageSkill:
      return <MonoBlock text={formatManageSkillInput(parsed)} />;

    case ToolType.ToolSearch:
      return <MonoBlock text={`Query: "${parsed.query || ''}"`} />;

    // TODO: Add structured input rendering for Think in the activity modal.
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

/**
 * Returns structured output rendering for a tool, or null if the tool has no
 * structured output (in which case the caller should render a fallback).
 */
export function getToolOutputBlock(
  toolCall: ProgressToolCall,
  parsed: ParsedProgressToolCall | null
): JSX.Element | null {
  if (parsed == null) {
    return null;
  }

  if (isMCPToolCall(parsed)) {
    return null;
  }

  switch (parsed.tool) {
    case ToolType.QueryWarehouseTool:
      return <MarkdownBlock markdown={toolCall.responseText?.trim() || 'No response available'} />;
    case ToolType.GetWarehouseOverviewTool:
      return fallbackOutputBlock(toolCall);

    case ToolType.LoadSkill: {
      const rawResponse = toolCall.responseText?.trim() || 'No skill content available';
      // responseText is XML-wrapped by the backend (see load_skill_tool.py).
      // Parse it into the unified skill-response shape so we can render using
      // the same SkillFileList pipeline as ManageSkill (SKILL.md frontmatter
      // is extracted into a structured name/description header by SkillMdBlock).
      const parsedResponse = parseLoadSkillResponse(rawResponse, parsed);
      if (!parsedResponse || !parsedResponse.success) {
        return <MonoBlock text={rawResponse} />;
      }
      return <SkillFileList parsed={parsedResponse} />;
    }

    case ToolType.ManageSkill: {
      const rawResponse = toolCall.responseText?.trim();
      const parsedResponse = rawResponse ? parseManageSkillResponse(rawResponse, parsed) : null;
      if (parsedResponse && !parsedResponse.success) {
        return <MonoBlock text={`Error: ${parsedResponse.message}`} />;
      }
      // Fall back to rendering just the input args when the response isn't
      // parseable (e.g. the tool is still pending or the backend wrapper is
      // missing) — users still see what the agent was trying to write.
      const renderable: ParsedSkillResponse = parsedResponse ?? {
        success: true,
        skillId: null,
        skillName: null,
        files: getManageSkillFiles(parsed),
        message: '',
      };
      if (renderable.files.length === 0) {
        return <MonoBlock text="No file contents available" />;
      }
      return <SkillFileList parsed={renderable} />;
    }

    case ToolType.RunCodeTool: {
      const raw = toolCall.responseText?.trim() || '';
      const hasChart = raw.includes('<bnch_chart>');
      const chartWarnings = [...raw.matchAll(/Warning: failed to save chart\(s\): [^\n]*/g)].map(
        (match) => match[0]
      );
      const cleaned = raw
        .replace(/Charts created \(embed these in your response using the tags below\):\n?/g, '')
        .replace(/<bnch_chart>[^<]*<\/bnch_chart>\n?/g, '')
        .replace(/Warning: failed to save chart\(s\): [^\n]*\n?/g, '')
        .trim();
      const output = [cleaned, ...chartWarnings]
        .filter((s) => s)
        .join('\n')
        .trim();
      if (!output && hasChart) {
        return <MonoBlock text="Chart created successfully" />;
      }
      if (!output) {
        return <MonoBlock text={raw || 'No response available'} />;
      }
      return <MonoBlock text={output} />;
    }

    case ToolType.ToolSearch:
      return fallbackOutputBlock(toolCall);

    // TODO: Add structured output rendering for these displayed tools.
    case ToolType.SearchTool:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.AttachmentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.IdsToModelsTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.Think:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.ResearchSubAgent:
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
      return null;
    default:
      assertNever(parsed);
      return null;
  }
}

function fallbackInputBlock(toolCall: ProgressToolCall): JSX.Element {
  const args = toolCall.args ?? {};
  const text = Object.keys(args).length > 0 ? formatArgsForDisplay(args) : 'No input parameters';
  return <MonoBlock text={text} />;
}

function fallbackOutputBlock(toolCall: ProgressToolCall): JSX.Element {
  return <MonoBlock text={toolCall.responseText?.trim() || 'No response available'} />;
}

// ============================================================================
// ToolDetail component
// ============================================================================

export interface ToolDetailProps {
  toolCall: ProgressToolCall;
}

function getToolOutputActions(parsed: ParsedProgressToolCall | null): JSX.Element | null {
  if (parsed == null || isMCPToolCall(parsed)) {
    return null;
  }
  if (
    parsed.tool === ToolType.QueryWarehouseTool &&
    parsed.query &&
    canUserCreateOrEditInsightsDashboards()
  ) {
    return <CreateDashboardFromQueryButton sqlQuery={parsed.query} />;
  }
  return null;
}

/**
 * Displays detailed information about a specific tool call.
 * Parses the generic ProgressToolCall into typed fields at this boundary
 * so that input/output formatting can use well-typed properties.
 */
export function ToolDetail({toolCall}: ToolDetailProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const parsed = parseProgressToolCall(toolCall);
  const outputActions = getToolOutputActions(parsed);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <BenchlingIcon icon={getToolIcon(toolCall.tool)} className={css(styles.toolIcon)} />
        <div className={css(styles.toolName)}>
          {getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status)}
        </div>
      </div>

      <div className={css(styles.content)}>
        <div className={css(styles.section)}>
          <div className={css(styles.sectionLabel)}>Input:</div>
          {getToolInputBlock(toolCall, parsed) ?? fallbackInputBlock(toolCall)}
        </div>
        <div className={css(styles.section)}>
          <Group position="apart" spacing="md">
            <div className={css(styles.sectionLabel)}>Output:</div>
            {outputActions && <Group spacing="sm">{outputActions}</Group>}
          </Group>
          {getToolOutputBlock(toolCall, parsed) ?? fallbackOutputBlock(toolCall)}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '4px',
  },

  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
  },

  toolIcon: {
    fontSize: '18px',
    color: GRAY_DARKER,
  },

  toolName: {
    ...FONT_PROXIMA_18,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },
});
